<template>
    <div v-if="renderPage" class="page container" data-page="userDetail">

        <!--Breadcrumbs-->
        <div class="breadcrumbs">
            <router-link to="/">Home</router-link>
            <span class="divider"><ion-icon name="chevron-forward"></ion-icon></span>
            <router-link to="/members">Members</router-link>
            <span class="divider"><ion-icon name="chevron-forward"></ion-icon></span>
            <router-link v-if="user !== null" :to="userUrl" class="active">{{ user.displayName }}</router-link>
        </div>
        
        <div v-if="user !== null" class="pure-g">

            <!-- Left Side -->
            <div class="pure-u-1 pure-u-lg-6-24 left">

                <div class="image top">
                    <div>
                        <div
                            @click.prevent="
                                currentTab = 'Photos'
                                showImage(-1)
                            "
                            class="avatar custom mb-2"
                            :style="'background-image:url('+ user.photoURL +'?width=500&height=500);'"
                        >
                            <MemberStatus :memberId="user.uid"></MemberStatus>
                        </div>

                        <h1 class="name mb-2">{{ user.displayName }}</h1>

                        <template v-if="userHasLocation(user)">
                            <p class="location mb-2">{{ user.location.city.name }}, {{ user.location.state.name }}</p>
                        </template>

                        <div class="status mb-2">
                            <span v-if="user.displayName === 'Anand Davaasuren'" class="badge green">Organizer</span>
                            <span v-else-if="user.isStaff" class="badge green">{{ user.staffTitle }}</span>
                            <span v-else-if="user.isMember" class="badge yellow">Member</span>
                            <span v-else class="badge grey">User</span>
                        </div>

                        <div class="pure-g counts mb-2">
                            <div class="pure-u-1-3">
                                <p class="om-label">Friends</p>
                                <h3>{{ counts.friends }}</h3>
                            </div>
                            <div class="pure-u-1-3">
                                <p class="om-label">Events</p>
                                <h3>{{ counts.events }}</h3>
                            </div>
                            <div class="pure-u-1-3">
                                <p class="om-label">Workshops</p>
                                <h3>{{ counts.workshops }}</h3>
                            </div>
                            <div class="pure-u-1-3"></div>
                        </div>

                        <div class="socialIcons">
                            <template v-if="user.instagram">
                                <a :href="`https://www.instagram.com/${user.instagram.username}/`" target="_blank" class="instagram hint hint--top" :data-hint="`@${user.instagram.username}`">
                                    <ion-icon name="logo-instagram"></ion-icon>
                                </a>
                            </template>
                            <template v-if="isFacebookProfileLink">
                                <a :href="user.facebook.profile.link" target="_blank" class="facebook hint hint--top" data-hint="Go to Facebook profile">
                                    <ion-icon name="logo-facebook"></ion-icon>
                                </a>
                            </template>
                        </div>

                        <p class="joined">Joined on {{ user.timestamp | joinedOn }}</p>

                        <MemberActions
                            :key="actionsKey"
                            :member="user"
                            :hideProfile="true"
                            @change-tab="changeTab"
                        ></MemberActions>
                    </div>
                </div>

            </div>

            <!-- Right Side -->
            <div class="pure-u-1 pure-u-lg-18-24 right">

                <!--User Tabs-->
                <nav class="nav mBottom userDetailNav">
                    <template v-for="(tab, index) in tabs">
                        <a
                            :key="index"
                            href="#0"
                            @click.prevent="currentTab = tab"
                            :class="{active: currentTab === tab}"
                        >{{ tab }}</a>
                    </template>

                    <a
                        v-if="isProfileMine"
                        href="#0"
                        @click.prevent="currentTab = 'Settings'"
                        :class="{active: currentTab === 'Settings'}"
                        class="settings align-right"
                    >Settings</a>
                    <a
                        v-if="isProfileMine"
                        href="#0"
                        @click.prevent="currentTab = 'Files'"
                        :class="{active: currentTab === 'Files'}"
                        class="settings align-right"
                    >Files</a>
                </nav>

                <!--Photos-->
                <div class="tab" v-if="currentTab === 'Photos'">

                    <template v-if="getUserMedia.length > 0">

                        <!--Add Photo or Video-->
                        <template v-if="isProfileMine">
                            <div class="pure-u-1 addPhotoAreaActions">
                                <template v-if="$store.state.currentUser !== null">
                                    <FileUpload
                                        :key="`file_uploader_photos_${fileUploaderKey}`"
                                        ref="fileUpload"
                                        :path="'files/'"
                                        :uid="$store.state.currentUser.uid"
                                        @upload-success="syncAllUploadedFiles"
                                    ></FileUpload>
                                </template>
                            </div>
                        </template>

                        <div class="grid-layout files gallery">

                            <!-- <div v-if="instaIntegration" class="pure-u-1 block">
                                <h5>Show Instagram Images: <a :href="authUrl" target="_blank" class="btn green">Connect Instagram</a></h5>
                                <p>{{ authUrl }}</p>
                                <p v-if="errorMsg !== ''" class="error">{{ errorMsg }}</p>
                            </div> -->

                            <!--User Photos & Videos-->
                            <div
                                v-for="(item, i) in getUserMedia"
                                :key="`media_${i}`"
                                class="grid-item"
                                :class="`grid-item-${i + 1}`"
                            >
                                <div class="userFileItem">
                                    <div
                                        class="image"
                                        :style="'background-image:url('+ item.thumb +');'"
                                    >
                                        <div
                                            class="galleryItem"
                                            @click.prevent="showImage(i)"
                                        ></div>
                                    </div>
                                    <template v-if="isProfileMine">
                                        <div class="fileActions">
                                            <a v-if="!item.isProfilePic" @click.prevent="deletePost(item)" class="iconBtnCircle">
                                                <ion-icon name="trash"></ion-icon>
                                            </a>
                                        </div>
                                    </template>
                                </div>
                            </div>

                        </div>
                        
                        <div class="gallery-lightbox">
                            <LightBox
                                ref="photos"
                                :media="getUserMedia"
                                :showLightBox="true"
                                :startAt="showImageAt"
                                v-if="showMedia"
                                @onClosed="showMedia = false"
                            >
                                <template slot="close">
                                    <ion-icon name="close"></ion-icon>
                                </template>
                                <template slot="previous">
                                    <ion-icon name="arrow-back"></ion-icon>
                                </template>
                                <template slot="next">
                                    <ion-icon name="arrow-forward"></ion-icon>
                                </template>
                            </LightBox>
                        </div>
                    </template>

                    <template v-else>
                        <div class="empty">
                            <p>User has not added any Photos or Videos yet.</p>
                        </div>
                    </template>
                    
                </div>

                <!--Events-->
                <div class="tab" v-else-if="currentTab === 'Events'">
                    <div class="pure-g">
                        <Events
                            grid="3"
                            type="userJoined"
                            :userId="user.uid"
                            @count="(count) => counts.events = count"
                        ></Events>
                    </div>
                </div>

                <!--Workshops-->
                <div class="tab" v-else-if="currentTab === 'Workshops'">
                    <div class="pure-g">
                        <Workshops
                            grid="3"
                            type="userJoined"
                            :userId="user.uid"
                        ></Workshops>
                    </div>
                </div>

                <!--Friends-->
                <div class="tab" v-show="currentTab === 'Friends'">
                    <Friends
                        ref="friends"
                        :uid="user.uid"
                    ></Friends>
                </div>

                <!--Files-->
                <div class="tab" v-if="currentTab === 'Files'">
                    <template v-if="$store.state.currentUser !== null">
                        <FileUpload
                            :key="`file_uploader_${fileUploaderKey}`"
                            ref="fileUpload"
                            :path="'files/'"
                            :uid="$store.state.currentUser.uid"
                        ></FileUpload>
                        <!--@delete-success="filesKey += 1" -->
                        <!--@upload-success=""-->
                    </template>

                    <Files
                        :key="`files_${filesKey}`"
                        @delete-file="(file) => {
                            $refs.fileUpload.deleteFiles(file)
                        }"
                    ></Files>
                </div>

                <!--Settings-->
                <div class="tab" v-if="isProfileMine && currentTab === 'Settings'">

                    <div class="block">
                        <h4 class="formLabel">Social:</h4>

                        <p class="om-label">Facebook</p>
                        <input type="text" placeholder="Facebook profile: @username" />

                        <template v-if="!instaIntegration">
                            <p class="om-label">Instagram:</p>

                            <input v-if="user.instagram.username" type="text" v-model="user.instagram.username" placeholder="Instagram Profile Url" />

                            <label class="switch">
                                <span>Off</span>
                                <input type="checkbox" v-model="user.instagram.status" @change.prevent="disconnectInstagram">
                                <i></i>
                                <span>On</span>
                            </label>

                            <!-- <a @click.prevent="disconnectInstagram" href="#0" class="btn green">Disconnect Instagram</a> -->
                        </template>
                    </div>
                    
                </div>

            </div>

        </div>
    </div>
</template>

<script>
import moment from 'moment'
const fb = require('@/firebaseConfig.js')
import Events from '@/components/Events'
import Workshops from '@/components/Workshops'
import MemberStatus from '@/components/MemberStatus'
import MemberActions from '@/components/MemberActions'
import LightBox from 'vue-image-lightbox'

import FileUpload from '@/components/uploader/FileUpload'
import Files from '@/components/user/Files'
import Friends from '@/components/user/Friends'

export default {
    metaInfo(){
        return{
            title: this.user.displayName,
            titleTemplate: '%s | Members | OneMgl',
            meta: [
                {
                    property: 'og:title',
                    content: this.user.displayName,
                    template: '%s | Members | OneMgl',
                },
                {
                    property: 'og:image',
                    content: this.user.photoURL
                },
                {
                    property: 'og:url',
                    content: `https://www.onemgl.com/user/${this.user.uid}`
                },
                {
                    property: 'twitter:title',
                    content: this.user.displayName,
                    template: '%s | Members | OneMgl'
                },
                {
                    property: 'twitter:image',
                    content: this.user.photoURL
                }
            ]
        }
    },

    components: {
        Events,
        Workshops,
        MemberStatus,
        MemberActions,
        LightBox,
        FileUpload,
        Files,
        Friends
    },

    data(){
        return {
            actionsKey: 0,
            currentTab: 'Photos',
            tabs: [
                'Photos',
                'Events',
                'Workshops',
                'Friends'
            ],
            user: null,
            userUrl: '',
            media: [],
            userMedia: [],
            showMedia: false,
            counts: {
                friends: 0,
                events: 0,
                workshops: 0
            },
            authUrl: '',
            instaIntegration: false,
            appId: '221438092679908', // '676698649553280',
            appSecret: '2d159a6f3f1a5a2b92ce8edf10576448', // 'f5fe9f37640ac103c4633e47e7eb7440',
            redirectUri: 'https://www.onemgl.com/auth',
            base: 'https://graph.instagram.com',
            errorMsg: '',
            renderPage: false,
            fileUploaderKey: 0,
            filesKey: 0
        }
    },

    watch: {
        "$route.params.id"(val) {
            this.refresh()
            this.init()
        }
    },

    async mounted(){
        this.renderPage = true
        let userId = this.$route.params.id

        await this.getUserProfile()   
        await this.getUserCounts()
        await this.$bind('userMedia', fb.db.collection('users').doc(userId).collection('posts').orderBy('timestamp', 'desc'))
    },

    computed: {
        isProfileMine(){
            if(this.$store.state.currentUser !== null && this.user !== null){
                if(this.user.uid === this.$store.state.currentUser.uid){
                    return true
                }
            }
            return false
        },
        isFacebookProfileLink(){
            if(typeof this.user.facebook !== 'undefined'){
                if(typeof this.user.facebook.profile !== 'undefined'){
                    if(typeof this.user.facebook.profile.link !== 'undefined'){
                        return true
                    }
                }
            }
            return false
        },
        getUserFriends(){
            let userFriends = []

            if(typeof this.user.friends !== 'undefined'){
                Object.keys(this.user.friends).forEach(friendUid => {
                    let friendStatus = this.user.friends[friendUid]
                    if(friendStatus.isFriends){
                        let friendData = {}

                        friendData = this.$store.state.allUsers[friendUid]
                        friendData.isFriends = friendStatus.isFriends
                        friendData.status = friendStatus.status
                        friendData.timestamp = friendStatus.timestamp

                        userFriends.push(friendData)
                    }
                })
            }

            return userFriends
        },
        getUserMedia(){
            let allUserMedia = []

            // User Posts
            if(this.userMedia.length > 0){
                this.userMedia.forEach(post => {
                    let files = post.files
                    let file = null

                    if(files.length > 0){
                        file = files[0]
                    }

                    if(file !== null){
                        if(file.type === 'image'){
                            allUserMedia.push({
                                postId: post.id,
                                postUid: post.uid,
                                id: file.uid,
                                thumb: file.small.url,
                                src: file.original.url,
                                caption: file.name,
                                provider: 'OneMgl',
                                isProfilePic: false
                            })
                        }else if(file.type === 'video'){
                            allUserMedia.push({
                                postId: post.id,
                                postUid: post.uid,
                                id: file.uid,
                                thumb: file.poster.url,
                                sources: [{
                                    src: file.url,
                                    type: file.fileType
                                }],
                                caption: file.name,
                                provider: 'OneMgl',
                                type: 'video',
                                width: '100%',
                                height: 650,
                                autoplay: true,
                                isProfilePic: false
                            })
                        }
                    }
                })
            }

            // Default User Profile Pic
            if(this.user.photoURL){
                allUserMedia.push({
                    thumb: this.user.photoURL + '?width=500&height=500',
                    src: this.user.photoURL + '?width=1024&height=1024',
                    caption: 'OneMgl Profile Picture',
                    provider: 'facebook',
                    isProfilePic: true
                })
            }

            console.log('All User Media: ', allUserMedia)

            return allUserMedia
        }
    },

    methods: {
        // Init 
        async init(){
            let userId = this.$route.params.id

            await this.getUserProfile()   
            await this.getUserCounts()
            await this.$bind('userMedia', fb.db.collection('users').doc(userId).collection('posts').orderBy('timestamp', 'desc'))

            // this.checkInstagram()
            
            // this.authUrl = `https://api.instagram.com/oauth/authorize?client_id=${this.appId}&redirect_uri=${this.redirectUri}&scope=user_profile,user_media&response_type=code`

            // if(this.$route.query.authCode){
            //     this.getAuthAccessToken(this.$route.query.authCode)
            // }
        },
        refresh(){
            // Reset fields
            this.actionsKey = 0
            this.currentTab = 'Photos'
            this.user = null
            this.userUrl = ''
            this.media = []
            this.showMedia = false

            this.renderPage = false

            this.$nextTick().then(() => {
                this.renderPage = true
            })
        },

        // Profile
        changeTab(tab){
            this.currentTab = tab
        },
        async getUserProfile(){
            let userId = this.$route.params.id
            this.userUrl = `/users/${userId}`
            this.user = this.$store.getters.getUserProfileById(userId)
            this.actionsKey += 1

            // Check if instagram
            // if(typeof this.user.instagram !== 'undefined'){
                
            //     this.getInstaAccount(this.user.instagram)

            //     console.log('Found insta data: ', this.user.instagram)
            //     console.log('Insta access token will expire in: ', moment.duration(this.user.instagram.expires_in, 'seconds').humanize())
            // }

            // this.getUserMedia()
        },
        showImage(index){
            if(index === -1){
                index = this.userMedia.length
            }
            this.showImageAt = index
            this.showMedia = true
        },
        async getUserCounts(){

            let userEventsRef = fb.usersCollection.doc(this.user.uid).collection('joinedEvents')
            let userWorkshopsRef = fb.usersCollection.doc(this.user.uid).collection('joinedWorkshops')

            let userEventsCount = await userEventsRef.get().then(snap => {
                let count = 0
                snap.forEach(doc => {
                    count += 1
                })
                return count
            })
            let userWorkshopsCount = await userWorkshopsRef.get().then(snap => {
                let count = 0
                snap.forEach(doc => {
                    count += 1
                })
                return count
            })
            let userFriendsCount = this.$refs.friends.friendsCount

            this.counts = {
                friends: userFriendsCount,
                events: userEventsCount,
                workshops: userWorkshopsCount
            }
        },
        syncAllUploadedFiles(files){

            console.log('Finished uploading and generating all files: ', files)

            let post = {
                uid: this.$store.state.currentUser.uid,
                timestamp: fb.firebase.firestore.FieldValue.serverTimestamp(),
                type: 'image',
                files: []
            }

            for(let i = 0; i < files.length; i++){
                let file = files[i]
                delete file.timestamp
                post.files.push(file)
            }

            // Add to Profile
            fb.db.collection('users').doc(post.uid).collection('posts').add(post).then(doc => {

                this.$notify({
                    group: 'globalNotifications',
                    title: 'Successfully added a new post to your profile.'
                })
                this.fileUploaderKey += 1
            
            }).catch(err => {
                console.log('Error adding a new profile posst!', err)

                this.$notify({
                    group: 'globalNotifications',
                    title: 'Error adding a new post to your profile!'
                })
            })
        },
        deletePost(post){
            if(confirm('Are you sure you want to delete this post?')){

                console.log('Delete post: ', post)

                fb.db.collection('users').doc(post.postUid).collection('posts').doc(post.postId).delete().then(() => {
                    
                    this.$notify({
                        group: 'globalNotifications',
                        title: 'Successfully removed post.'
                    })

                }).catch(err => {
                    
                    console.log('Error removing post: ', err)

                    this.$notify({
                        group: 'globalNotifications',
                        title: 'Error removing this post!'
                    })
                })
            }
        },

        // Instagram
        async getAuthAccessToken(code){
            
            let data = {
                client_id: this.appId,
                client_secret: this.appSecret,
                grant_type: 'authorization_code',
                redirect_uri: this.redirectUri,
                code: code
            }
            let url = 'https://api.instagram.com/oauth/access_token'
            let dataUrl = `client_id=${data.client_id}&client_secret=${data.client_secret}&grant_type=authorization_code&redirect_uri=${data.redirect_uri}&code=${data.code}`
            let options = {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            }

            await this.$http.post(url, dataUrl, options).then(async res => {
                if(res.status === 200){
                    if(res.body.access_token){

                        let resData = res.body

                        console.log('1. Auth access token response: ', resData)

                        await this.exchangeToken(resData)
                    }
                }
            }).catch(err => {
                console.log('1. Auth access token error: ', err)

                if(err.body.error_message){
                    this.errorMsg = err.body.error_message
                }
            })
        },
        async exchangeToken(tokenData){

            let data = {
                grant_type: 'ig_exchange_token',
                client_secret: this.appSecret,
                access_token: tokenData.access_token,
                user_id: tokenData.user_id
            }
            let url = 'https://graph.instagram.com/access_token'
            let dataUrl = `${url}?grant_type=ig_exchange_token&client_secret=${data.client_secret}&access_token=${data.access_token}`
            let options = {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            }

            await this.$http.get(dataUrl, options).then(async res => {
                if(res.status === 200){
                    if(res.body.access_token){

                        let resData = res.body

                        console.log('2. Exchange token response: ', resData)

                        let newTokenData = {
                            access_token: resData.access_token,
                            token_type: resData.token_type,
                            expires_in: resData.expires_in,
                            user_id: tokenData.user_id
                        }

                        await this.saveAccessToken(newTokenData)
                    }
                }
            }).catch(err => {
                console.log('2. Exchange token error: ', err)

                if(err.body.error_message){
                    this.errorMsg = err.body.error_message
                }
            })
        },
        async refreshToken(tokenData){

            let data = {
                grant_type: 'ig_refresh_token',
                access_token: tokenData.access_token
            }
            let url = 'https://graph.instagram.com/refresh_access_token'
            let dataUrl = `grant_type=ig_refresh_token&access_token=${tokenData.access_token}`
            let options = {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            }

            await this.$http.post(url, dataUrl, options).then(async res => {
                if(res.status === 200){
                    if(res.body.access_token){

                        let resData = res.body
                        
                        console.log('0. Refresh access token: ', resData)

                        let newTokenData = {
                            access_token: resData.access_token,
                            token_type: resData.token_type,
                            expires_in: resData.expires_in
                        }

                        await this.saveAccessToken(newTokenData)
                    }
                }
            }).catch(err => {
                console.log('0. Refresh access token error: ', err)
            })
        },
        async saveAccessToken(tokenData){

            let userRef = fb.usersCollection.doc(this.$store.state.currentUser.uid)

            let updates = {
                instagram: tokenData
            }

            this.user.instagram = tokenData

            await userRef.update(updates).then(async () => {
                
                console.log('3. Saved long lived access token: ', updates)

                await this.getInstaAccount(tokenData)

            }).catch(async err => {

                console.log('3. Error saving long lived access token: ', err)

                await this.getInstaAccount(tokenData)
            })
        },
        async getInstaAccount(tokenData){

            let userRef = fb.usersCollection.doc(this.$store.state.currentUser.uid)

            console.log('Get Insta Account: ', tokenData)

            // Check Username
            if(typeof this.user.instagram.username === 'undefined'){

                await this.$http.get(`${this.base}/me?fields=id,username&access_token=${tokenData.access_token}`).then(res => {
                    if(res.status === 200){
                        if(res.body.username){

                            console.log('4.1 Get username: ', res.body)
                            
                            let updates = {
                                instagram: this.user.instagram
                            }
                            
                            updates.instagram.username = res.body.username

                            this.user.instagram.username = res.body.username

                            userRef.update(updates)
                        }
                    }
                }).catch(err => {
                    console.log('4.1 Get username error: ', err)
                })
            }

            // Check User Media
            if(typeof this.user.instagram.media === 'undefined'){

                await this.$http.get(`${this.base}/me/media?fields=id,caption&access_token=${tokenData.access_token}`).then(async res => {
                    if(res.status === 200){
                        if(res.body.data){
                            
                            console.log('4.2 Get user media ids: ', res.body)

                            await this.getAllInstaMedia(res.body.data)
                        }
                    }
                }).catch(err => {
                    console.log('4.2 Get user media ids error: ', err)
                })
            }
        },
        async getAllInstaMedia(data){
            
            for(let i = 0; i < data.length; i++){

                let mediaData = await this.getMediaData(data[i].id)

                let tempMedia = {
                    id: data[i].id,
                    caption: data[i].caption,
                    media: mediaData,
                    src: mediaData.media_url,
                    provider: 'instagram'
                }

                if(mediaData.thumbnail_url){
                    tempMedia.thumb = mediaData.thumbnail_url
                }else{
                    tempMedia.thumb = mediaData.media_url
                }

                this.media.push(tempMedia)

                if(this.media.length === data.length){
                    await this.saveInstaMedia()
                }
            }
        },
        async getMediaData(mediaId){
            return await this.$http.get(`${this.base}/${mediaId}?fields=id,media_type,media_url,username,timestamp,thumbnail_url,permalink&access_token=${this.user.instagram.access_token}`).then(res => {
                if(res.status === 200){
                    if(res.body.media_url){
                        return res.body
                    }
                }
            })
        },
        async saveInstaMedia(){

            console.log('Save insta media started: ', this.media)

            let userRef = fb.usersCollection.doc(this.$store.state.currentUser.uid)

            this.user.instagram.media = this.media

            let updates = {
                instagram: this.user.instagram
            }
            updates.instagram.media = []

            for(let i = 0; i < this.media.length; i++){
                let media = this.media[i]

                let mediaUpdates = {
                    caption: media.caption ? media.caption : null,
                    id: media.id,
                    src: media.src,
                    thumb: media.thumb,
                    provider: 'instagram',
                    media: {
                        id: media.media.id,
                        media_type: media.media.media_type,
                        media_url: media.media.media_url,
                        permalink: media.media.permalink,
                        timestamp: media.media.timestamp,
                        username: media.media.username
                    }
                }
                if(media.media.thumbnail_url){
                    mediaUpdates.media.thumbnail_url = media.media.thumbnail_url
                }
                
                updates.instagram.media.push(mediaUpdates)
            }

            console.log('5.0 Save Insta Media: ', this.user.instagram)

            await userRef.update(updates).then(() => {

                console.log('5.1 Saved all user media.', updates)

            }).catch(err => {

                console.log('5.1 Error saving all user media.', err)
            })
        },
        checkInstagram(){
            if(this.$store.state.currentUser !== null){
                if(this.user !== null){
                    if(this.user.uid === this.$store.state.currentUser.uid){

                        if(this.$route.query.remove){
                            
                            this.instaIntegration = false
                            this.user.instagram.status = false

                        }else if(typeof this.user.instagram !== 'undefined'){
                            if(typeof this.user.instagram.access_token !== 'undefined'){
                            
                                this.instaIntegration = true
                                this.user.instagram.status = true

                                console.log('Check instagram access token: ', this.user.instagram.expires_in)
                            }
                        }
                    }
                }
            }
        },
        disconnectInstagram(){
            // if(typeof this.user.instagram.status !== 'undefined'){
            //     if(this.user.instagram.status === true){
            //         this.$router.push('/auth?remove=true')
            //     }else{
            //         window.open(this.authUrl, '_blank')
            //     }
            // }else{
            //     window.open(this.authUrl, '_blank')
            // }
        },
        userHasLocation(member){
            if(typeof member.location !== 'undefined' && member.location !== null){
                if(typeof member.location.city !== 'undefined' && member.location.city !== null){
                    return true
                }
            }
            return false
        }
    },
    filters: {
        joinedOn(value){
            return moment(value.toDate()).local().format('MMMM YYYY')
        }
    }
}
</script>


// WEBPACK FOOTER //
// src/views/UserDetail.vue